import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { WhatsAppMessage, SendMessageData } from '@/types/whatsapp';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

interface MessageFilters {
  direction?: 'INCOMING' | 'OUTGOING';
  type?: string;
  leadId?: string;
  userId?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}

export function useWhatsAppMessages(filters: MessageFilters = {}) {
  return useQuery<WhatsAppMessage[]>({
    queryKey: ['whatsapp', 'messages', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });

      const res = await fetch(`${API_BASE_URL}/whatsapp/logs?${params}`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        throw new Error('Failed to fetch WhatsApp messages');
      }
      const data = await res.json();
      return data.data || data;
    },
  });
}

export function useWhatsAppMessagesInfinite(filters: Omit<MessageFilters, 'offset'> = {}) {
  return useInfiniteQuery<WhatsAppMessage[]>({
    queryKey: ['whatsapp', 'messages', 'infinite', filters],
    queryFn: async ({ pageParam = 0 }) => {
      const params = new URLSearchParams();
      Object.entries({ ...filters, offset: pageParam, limit: filters.limit || 50 }).forEach(
        ([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            params.append(key, value.toString());
          }
        }
      );

      const res = await fetch(`${API_BASE_URL}/whatsapp/logs?${params}`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        throw new Error('Failed to fetch WhatsApp messages');
      }
      const data = await res.json();
      return data.data || data;
    },
    getNextPageParam: (lastPage, pages) => {
      const limit = filters.limit || 50;
      if (lastPage.length < limit) return undefined;
      return pages.length * limit;
    },
    initialPageParam: 0,
  });
}

export function useSendMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: SendMessageData) => {
      const res = await fetch(`${API_BASE_URL}/whatsapp/send`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to send message');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp', 'messages'] });
      queryClient.invalidateQueries({ queryKey: ['whatsapp', 'statistics'] });
    },
  });
}

export function useSendMessageToUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { userId: string; content: string; type?: string }) => {
      const res = await fetch(`${API_BASE_URL}/whatsapp/send-to-user`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to send message to user');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp', 'messages'] });
      queryClient.invalidateQueries({ queryKey: ['whatsapp', 'statistics'] });
    },
  });
}

export function useWhatsAppStatistics() {
  return useQuery({
    queryKey: ['whatsapp', 'statistics'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE_URL}/whatsapp/statistics`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        throw new Error('Failed to fetch WhatsApp statistics');
      }
      const data = await res.json();
      return data.data || data;
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });
}
